import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, AlertTriangle, Archive, EyeOff, Eye, FileText,
  RotateCw, Skull, Shield, Clock, Filter, ChevronRight, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  efAdminLifecycleList,
  efAdminLifecycleImpact,
  efAdminLifecycleTransition,
  efAdminLifecycleBatchTransition,
  efAdminLifecycleAudit,
  efAdminLifecycleBulkTransition,
  efAdminLifecycleQuickFilter,
} from "../../lib/api";

/**
 * AdminEpsteinLifecycle — editorial rollback / unpublish / quarantine /
 * archive / republish hub. Five sub-views:
 *   • Published          (current public items)
 *   • Review queue       (draft / review / approved)
 *   • Unpublished        (was public, recoverable)
 *   • Quarantine         (emergency pulled)
 *   • Graveyard          (archived / rejected / superseded)
 *   • Audit log          (the trail)
 */

const OBJECT_TYPES = [
  "entity", "relationship", "source", "evidence_item", "fragment", "timeline_event", "batch",
];

const BUCKET_DEFS = [
  { key: "published", icon: Eye,       label: "Published",         accent: "#5DD4B3" },
  { key: "review",    icon: Clock,     label: "Review queue",      accent: "#9FA8D4" },
  { key: "withdrawn", icon: EyeOff,    label: "Unpublished",       accent: "#E0A526" },
  { key: "quarantine",icon: Shield,    label: "Quarantine",        accent: "#D92D20" },
  { key: "graveyard", icon: Skull,     label: "Graveyard",         accent: "#757065" },
  { key: "audit",     icon: FileText,  label: "Audit log",         accent: "#CBB892" },
];

const STATUS_COLORS = {
  draft:       "text-[#757065] border-[#454038]",
  review:      "text-[#9FA8D4] border-[#9FA8D4]/40",
  approved:    "text-[#CBB892] border-[#CBB892]/40",
  public:      "text-[#5DD4B3] border-[#5DD4B3]/40",
  unpublished: "text-[#E0A526] border-[#E0A526]/40",
  quarantined: "text-[#D92D20] border-[#D92D20]/40 bg-[#1a0808]",
  archived:    "text-[#757065] border-[#454038]",
  rejected:    "text-[#757065] border-[#454038]",
  superseded:  "text-[#757065] border-[#454038]",
};

const SEVERITY_OPTIONS = [
  ["", "—"],
  ["legal_concern", "Legal concern"],
  ["wrong_person", "Wrong person/entity"],
  ["source_dispute", "Source dispute"],
  ["evidence_conflict", "Evidence conflict"],
  ["wording_concern", "Wording concern"],
  ["emergency_pull", "Emergency pull"],
];

// Map quarantine bucket to the canonical lifecycle state.
const QUARANTINE_BUCKET = "quarantine";

const QUICK_FILTERS = [
  { key: "quarantined_by_severity",   label: "Quarantined (any severity)" },
  { key: "withdrawn_last_7d",         label: "Withdrawn / archived in last 7d" },
  { key: "has_downstream_public_deps",label: "Has downstream public deps" },
  { key: "restored_from_quarantine",  label: "Restored from quarantine" },
];

export const AdminEpsteinLifecycle = ({ token, userToken = null }) => {
  const [bucket, setBucket] = useState("published");
  const [results, setResults] = useState({});
  const [auditRows, setAuditRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);

  // V2: multi-select + quick filters
  const [selected, setSelected] = useState({}); // { `${type}:${id}`: true }
  const [bulkAction, setBulkAction] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [quickFilterKey, setQuickFilterKey] = useState("");
  const [quickFilterRows, setQuickFilterRows] = useState([]);
  const [quickFilterSeverity, setQuickFilterSeverity] = useState("");

  const clearSelection = () => setSelected({});

  const toggleSelect = (objType, objId) => {
    const k = `${objType}:${objId}`;
    setSelected((s) => {
      const n = { ...s };
      if (n[k]) delete n[k]; else n[k] = true;
      return n;
    });
  };
  const selectedCount = Object.keys(selected).length;

  const load = useCallback(async () => {
    setLoading(true);
    setQuickFilterKey("");
    setQuickFilterRows([]);
    clearSelection();
    try {
      if (bucket === "audit") {
        const r = await efAdminLifecycleAudit(token, { limit: 200 }, userToken);
        setAuditRows(r.audit || []);
      } else {
        const params = bucket === QUARANTINE_BUCKET
          ? { status: "quarantined", limit: 200 }
          : { bucket, limit: 200 };
        const r = await efAdminLifecycleList(token, params, userToken);
        setResults(r.results || {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [bucket, token, userToken]);

  const applyQuickFilter = async (key) => {
    setLoading(true);
    setQuickFilterKey(key);
    clearSelection();
    try {
      const opts = key === "quarantined_by_severity" && quickFilterSeverity
        ? { severity: quickFilterSeverity } : {};
      const r = await efAdminLifecycleQuickFilter(token, key, opts, userToken);
      setQuickFilterRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick filter failed");
    } finally { setLoading(false); }
  };

  const runBulk = async () => {
    if (selectedCount === 0) { toast.error("Pick at least one item."); return; }
    if (!bulkAction) { toast.error("Pick the new status."); return; }
    if (bulkReason.trim().length < 4) { toast.error("Provide a reason (4+ chars)."); return; }
    const items = Object.keys(selected).map((k) => {
      const [object_type, ...rest] = k.split(":");
      return { object_type, object_id: rest.join(":") };
    });
    setBulkBusy(true);
    try {
      const r = await efAdminLifecycleBulkTransition(token, {
        items, new_status: bulkAction, reason: bulkReason.trim(),
      }, userToken);
      toast.success(`${r.count_transitioned} transitioned`);
      setBulkAction("");
      setBulkReason("");
      clearSelection();
      if (quickFilterKey) await applyQuickFilter(quickFilterKey);
      else load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk transition failed");
    } finally { setBulkBusy(false); }
  };

  useEffect(() => { load(); }, [load]);

  const totalCount = useMemo(
    () => Object.values(results).reduce((a, b) => a + b.length, 0),
    [results],
  );

  return (
    <div data-testid="ef-admin-lifecycle-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Shield size={11} /> // EDITORIAL LIFECYCLE
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Rollback &amp; quarantine</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Pull any public item from the graph in one click. Items move between Published →
            Unpublished / Quarantine / Graveyard without deletion. Every transition is logged.
          </p>
        </div>
        <button onClick={load} data-testid="ef-lifecycle-refresh"
          className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Bucket tabs */}
      <div className="flex overflow-x-auto whitespace-nowrap gap-1 mb-3 border-b border-[#2D2A26]">
        {BUCKET_DEFS.map(({ key, icon: Icon, label, accent }) => {
          const active = bucket === key;
          return (
            <button key={key} onClick={() => setBucket(key)}
              data-testid={`ef-lifecycle-bucket-${key}`}
              className={"px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 flex items-center gap-1 " +
                (active ? "text-[#E4DFD3]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}
              style={active ? { borderColor: accent, color: accent } : {}}>
              <Icon size={11} /> {label}
            </button>
          );
        })}
      </div>

      {/* Quick filters bar */}
      {bucket !== "audit" && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 flex flex-wrap items-center gap-2"
             data-testid="ef-lifecycle-quick-filters">
          <Filter size={11} className="text-[#CBB892]" />
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">quick filters</span>
          {QUICK_FILTERS.map((qf) => (
            <button key={qf.key} onClick={() => applyQuickFilter(qf.key)}
              data-testid={`ef-lifecycle-qf-${qf.key}`}
              className={"font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-1 " +
                (quickFilterKey === qf.key
                  ? "text-[#D92D20] border-[#D92D20]"
                  : "text-[#A39E93] border-[#454038] hover:border-[#D92D20]")}>
              {qf.label}
            </button>
          ))}
          {quickFilterKey === "quarantined_by_severity" && (
            <select value={quickFilterSeverity}
              onChange={(e) => { setQuickFilterSeverity(e.target.value); applyQuickFilter("quarantined_by_severity"); }}
              data-testid="ef-lifecycle-qf-severity"
              className="bg-[#0F0F0F] border border-[#454038] text-[#A39E93] px-2 py-1 font-mono text-[9px]">
              {SEVERITY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          )}
          {quickFilterKey && (
            <button onClick={load}
              data-testid="ef-lifecycle-qf-clear"
              className="ml-auto font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#E4DFD3]">
              clear filter
            </button>
          )}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedCount > 0 && bucket !== "audit" && (
        <div className="border border-[#D92D20]/60 bg-[#1a0808] px-3 py-2 mb-3 flex flex-wrap items-center gap-2 sticky top-0 z-10"
             data-testid="ef-lifecycle-bulk-bar">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            {selectedCount} selected
          </span>
          <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}
            data-testid="ef-lifecycle-bulk-action"
            className="bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1 font-mono text-[10px]">
            <option value="">— bulk action —</option>
            <option value="unpublished">→ unpublished</option>
            <option value="quarantined">→ quarantined</option>
            <option value="archived">→ archived</option>
            <option value="review">→ restore to review</option>
            <option value="public">→ republish</option>
            <option value="rejected">→ rejected</option>
            <option value="superseded">→ superseded</option>
          </select>
          <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
            placeholder="reason (required)"
            data-testid="ef-lifecycle-bulk-reason"
            className="flex-1 min-w-[12rem] bg-[#0F0F0F] border border-[#454038] text-[#E4DFD3] px-2 py-1 font-mono text-xs" />
          <button onClick={runBulk} disabled={bulkBusy || !bulkAction || selectedCount === 0}
            data-testid="ef-lifecycle-bulk-submit"
            className="font-mono text-[10px] uppercase tracking-[0.3em] border border-[#D92D20] text-[#D92D20] hover:bg-[#220c0c] px-3 py-1 disabled:opacity-40">
            {bulkBusy ? "Working…" : "Apply"}
          </button>
          <button onClick={clearSelection}
            data-testid="ef-lifecycle-bulk-clear"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065] hover:text-[#E4DFD3]">
            clear
          </button>
        </div>
      )}

      {bucket === "audit" ? (
        <AuditTable rows={auditRows} />
      ) : quickFilterKey === "restored_from_quarantine" ? (
        <AuditTable rows={quickFilterRows} />
      ) : quickFilterKey ? (
        <section data-testid="ef-lifecycle-qf-results">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">
            {QUICK_FILTERS.find((q) => q.key === quickFilterKey)?.label} · {quickFilterRows.length}
          </div>
          <div className="border border-[#2D2A26] bg-[#131313]">
            {quickFilterRows.length === 0 && (
              <div className="p-6 text-center font-mono text-xs uppercase tracking-widest text-[#757065]">
                No matches.
              </div>
            )}
            {quickFilterRows.map((r) => (
              <ItemRow key={`${r._object_type}:${r.id}`}
                obj={r}
                selected={!!selected[`${r._object_type}:${r.id}`]}
                onToggleSelect={() => toggleSelect(r._object_type, r.id)}
                onAction={() => setActionTarget({
                  object_type: r._object_type, object_id: r.id,
                  current_status: r._lifecycle_status,
                  title: r.title || r.name || r.notes || r.id,
                })} />
            ))}
          </div>
        </section>
      ) : (
        <>
          {!loading && totalCount === 0 && (
            <div className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
                 data-testid="ef-lifecycle-empty">
              Nothing in this bucket.
            </div>
          )}
          {OBJECT_TYPES.map((ot) => {
            const rows = results[ot] || [];
            if (rows.length === 0) return null;
            return (
              <section key={ot} className="mb-4" data-testid={`ef-lifecycle-section-${ot}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mb-1">
                  {ot.replace(/_/g, " ")} · {rows.length}
                </div>
                <div className="border border-[#2D2A26] bg-[#131313]">
                  {rows.map((r) => (
                    <ItemRow key={r.id}
                      obj={r}
                      selected={!!selected[`${ot}:${r.id}`]}
                      onToggleSelect={() => toggleSelect(ot, r.id)}
                      onAction={() => setActionTarget({
                        object_type: ot, object_id: r.id, current_status: r._lifecycle_status,
                        title: r.title || r.name || r.notes || r.id,
                      })} />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      {actionTarget && (
        <TransitionModal
          target={actionTarget}
          token={token}
          userToken={userToken}
          onClose={() => setActionTarget(null)}
          onSuccess={() => { setActionTarget(null); load(); }}
        />
      )}
    </div>
  );
};


// ----------------------- internals -----------------------

const ItemRow = ({ obj, onAction, selected, onToggleSelect }) => {
  const cls = STATUS_COLORS[obj._lifecycle_status] || STATUS_COLORS.review;
  return (
    <div className={"px-3 py-2 border-b border-[#2D2A26] flex flex-wrap items-center gap-2 " +
                    (selected ? "bg-[#1a1108]" : "")}
         data-testid={`ef-lifecycle-row-${obj.id}`}>
      {onToggleSelect && (
        <input type="checkbox" checked={selected} onChange={onToggleSelect}
          data-testid={`ef-lifecycle-select-${obj.id}`}
          className="accent-[#D92D20]" />
      )}
      <span className={`font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 ${cls}`}>
        {obj._lifecycle_status}
      </span>
      <span className="font-mono text-[9px] text-[#757065] uppercase">{obj._object_type}</span>
      <span className="text-sm text-[#E4DFD3] truncate flex-1 min-w-[10rem]">{obj.title || obj.name || obj.notes || obj.id}</span>
      <span className="font-mono text-[9px] text-[#757065]">{obj.id}</span>
      {obj._public_dependents !== undefined && (
        <span className="font-mono text-[9px] text-[#E0A526] border border-[#E0A526]/40 px-1.5 py-0.5">
          {obj._public_dependents} pub deps
        </span>
      )}
      {obj.lifecycle_severity && (
        <span className="font-mono text-[9px] text-[#D92D20] border border-[#D92D20]/40 px-1.5 py-0.5">
          {obj.lifecycle_severity}
        </span>
      )}
      {obj.lifecycle_reason && (
        <span className="font-mono text-[9px] text-[#E0A526] italic truncate max-w-[18rem]">
          ⚠ {obj.lifecycle_reason}
        </span>
      )}
      <button onClick={onAction}
        data-testid={`ef-lifecycle-action-${obj.id}`}
        className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20]/60 hover:bg-[#1a0808] px-2 py-1">
        Actions
      </button>
    </div>
  );
};


const AuditTable = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-[#2D2A26] p-8 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
           data-testid="ef-lifecycle-audit-empty">
        No audit events recorded yet.
      </div>
    );
  }
  return (
    <div className="border border-[#2D2A26] bg-[#131313]" data-testid="ef-lifecycle-audit-table">
      {rows.map((r) => {
        const prev = STATUS_COLORS[r.prev_status] || STATUS_COLORS.review;
        const next = STATUS_COLORS[r.new_status] || STATUS_COLORS.review;
        return (
          <div key={r.id} className="px-3 py-2 border-b border-[#2D2A26] flex flex-wrap items-center gap-2"
               data-testid={`ef-audit-row-${r.id}`}>
            <span className="font-mono text-[9px] text-[#757065] uppercase">{r.object_type}</span>
            <span className="font-mono text-[9px] text-[#A39E93]">{r.object_id}</span>
            <span className={`font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 ${prev}`}>
              {r.prev_status}
            </span>
            <span className="text-[#757065]">→</span>
            <span className={`font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 ${next}`}>
              {r.new_status}
            </span>
            <span className="font-mono text-[10px] text-[#A39E93] flex-1 truncate">{r.reason}</span>
            {r.severity && (
              <span className="font-mono text-[9px] text-[#E0A526]">{r.severity}</span>
            )}
            <span className="font-mono text-[9px] text-[#757065]">{r.actor || "—"}</span>
            <span className="font-mono text-[9px] text-[#757065]">{r.created_at?.slice(0, 19)}</span>
          </div>
        );
      })}
    </div>
  );
};


const TransitionModal = ({ target, token, userToken, onClose, onSuccess }) => {
  const [impact, setImpact] = useState(null);
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [busy, setBusy] = useState(false);

  // Action options derive from current status.
  const ACTIONS = useMemo(() => {
    const opts = [];
    const cur = target.current_status;
    if (cur === "public")            opts.push("unpublished", "quarantined", "archived", "superseded");
    if (cur === "approved")          opts.push("public", "review", "unpublished", "archived");
    if (cur === "review" || cur === "draft") opts.push("approved", "rejected", "archived");
    if (cur === "unpublished")       opts.push("review", "public", "quarantined", "archived");
    if (cur === "quarantined")       opts.push("review", "unpublished", "archived");
    if (cur === "archived" || cur === "rejected" || cur === "superseded") opts.push("review");
    return Array.from(new Set(opts));
  }, [target.current_status]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await efAdminLifecycleImpact(
          token, target.object_type, target.object_id, userToken,
          { include_lists: true },
        );
        if (alive) setImpact(r);
      } catch (e) {
        if (alive) setImpact({ error: e?.response?.data?.detail || "impact failed" });
      }
    })();
    return () => { alive = false; };
  }, [target, token, userToken]);

  const submit = async () => {
    if (!newStatus) { toast.error("Pick the new status."); return; }
    if (reason.trim().length < 4) { toast.error("Provide a reason."); return; }
    const payload = {
      object_type: target.object_type,
      object_id: target.object_id,
      new_status: newStatus,
      reason: reason.trim(),
    };
    if (severity) payload.severity = severity;
    setBusy(true);
    try {
      await efAdminLifecycleTransition(token, payload, userToken);
      toast.success(`Moved to ${newStatus}`);
      onSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.reason === "source_reliability_block") {
        // Render block info inline
        setImpact((prev) => ({ ...(prev || {}), block: detail }));
        toast.error("Republish blocked: source replacement required");
      } else {
        toast.error(detail || "Transition failed");
      }
    } finally { setBusy(false); }
  };

  const isQuarantine = newStatus === "quarantined";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="ef-lifecycle-modal">
      <div className="bg-[#0F0F0F] border border-[#D92D20]/40 max-w-2xl w-full p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
              // editorial transition
            </div>
            <div className="font-display text-lg text-[#E4DFD3]">{target.title}</div>
            <div className="font-mono text-[10px] text-[#757065] mt-1">
              {target.object_type} · {target.object_id} · current: <span className="text-[#A39E93]">{target.current_status}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-[#757065] hover:text-[#E4DFD3]" data-testid="ef-lifecycle-modal-close">✕</button>
        </div>

        {/* Republish block UX */}
        {impact?.block && (
          <div className="border border-[#D92D20]/60 bg-[#1a0808] px-3 py-2 mb-3"
               data-testid="ef-lifecycle-modal-block">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1 flex items-center gap-1">
              <AlertTriangle size={11} /> republish blocked — {impact.block.reason}
            </div>
            <div className="text-sm text-[#E4DFD3] mb-2">{impact.block.message}</div>
            <div className="space-y-1">
              {(impact.block.blocking_sources || []).map((s) => (
                <div key={s.id} className="border border-[#2D2A26] bg-[#0F0F0F] px-2 py-1 font-mono text-xs">
                  <div className="text-[#E4DFD3]">{s.title}</div>
                  <div className="text-[#757065] break-all">{s.url}</div>
                  <div className="text-[#E0A526]">status: {s.reliability_status} {s.replacement_source_id ? `· replacement: ${s.replacement_source_id}` : "· no replacement attached"}</div>
                  {s.reliability_reason && <div className="text-[#A39E93] italic">“{s.reliability_reason}”</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Impact + drill-downs + delta preview */}
        {impact && !impact.error && (
          <div className="border border-[#2D2A26] bg-[#131313] p-3 mb-3 max-h-[26rem] overflow-y-auto"
               data-testid="ef-lifecycle-modal-impact">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2 flex items-center gap-2">
              <AlertTriangle size={10} className="text-[#E0A526]" /> public blast radius
              {impact.delta_if_unpublished !== 0 && (
                <span className="ml-auto font-mono text-[10px] text-[#D92D20]">
                  Δ if unpublished: {impact.delta_if_unpublished}
                </span>
              )}
              {impact.delta_if_published !== 0 && (
                <span className="ml-auto font-mono text-[10px] text-[#5DD4B3]">
                  Δ if republished: +{impact.delta_if_published}
                </span>
              )}
            </div>
            {/* Counts row */}
            <div className="flex flex-wrap gap-2 mb-2">
              {Object.entries(impact).map(([k, v]) => {
                if (!k.startsWith("public_") || typeof v !== "number") return null;
                return (
                  <span key={k} className="font-mono text-[10px] text-[#CBB892] border border-[#454038] px-2 py-1">
                    {k.replace(/_/g, " ")}: {v}
                  </span>
                );
              })}
              {impact.total_public_items !== undefined && (
                <span className="font-mono text-[10px] text-[#D92D20] border border-[#D92D20]/40 px-2 py-1">
                  total public items: {impact.total_public_items}
                </span>
              )}
            </div>
            {/* Drill-down lists */}
            {impact.dependencies && Object.keys(impact.dependencies).length > 0 && (
              <div className="space-y-2" data-testid="ef-lifecycle-modal-drilldown">
                {Object.entries(impact.dependencies).map(([cat, rows]) => (
                  <details key={cat} className="border border-[#2D2A26] bg-[#0F0F0F]" open>
                    <summary className="px-2 py-1 cursor-pointer font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] flex items-center gap-1">
                      <ChevronDown size={10} /> {cat.replace(/_/g, " ")} · {rows.length}
                    </summary>
                    <div className="px-2 pb-2">
                      {rows.map((r) => (
                        <div key={r.id} className="flex items-center gap-2 py-1 border-t border-[#1a1a1a]"
                             data-testid={`ef-lifecycle-modal-dep-${r.id}`}>
                          <span className="font-mono text-[9px] text-[#757065]">{r.id}</span>
                          <span className="text-xs text-[#E4DFD3] truncate flex-1">{r.title}</span>
                          {r.type && <span className="font-mono text-[9px] text-[#CBB892]">{r.type}</span>}
                          {r.kind && <span className="font-mono text-[9px] text-[#CBB892]">{r.kind}</span>}
                          {r.fragment_type && <span className="font-mono text-[9px] text-[#CBB892]">{r.fragment_type}</span>}
                          {r.evidence_type && <span className="font-mono text-[9px] text-[#CBB892]">{r.evidence_type}</span>}
                          {r.date && <span className="font-mono text-[9px] text-[#757065]">{r.date}</span>}
                        </div>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}
        {impact?.error && (
          <div className="border border-[#D92D20]/40 bg-[#1a0808] text-[#D92D20] text-xs px-3 py-2 mb-3">
            {impact.error}
          </div>
        )}

        {/* Action picker */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          {ACTIONS.map((s) => {
            const cls = STATUS_COLORS[s] || STATUS_COLORS.review;
            const selected = newStatus === s;
            return (
              <button key={s} onClick={() => setNewStatus(s)}
                data-testid={`ef-lifecycle-action-${s}`}
                className={"font-mono text-[10px] uppercase tracking-[0.3em] border px-2 py-2 " +
                  (selected ? `${cls} ring-2 ring-[#D92D20]` : cls)}>
                → {s}
              </button>
            );
          })}
        </div>

        {isQuarantine && (
          <div className="mb-3">
            <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">
              quarantine severity
            </label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)}
              data-testid="ef-lifecycle-severity"
              className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono">
              {SEVERITY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        )}

        <div className="mb-3">
          <label className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] block mb-1">
            reason (required, recorded in audit log)
          </label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            data-testid="ef-lifecycle-reason"
            placeholder="Why are you moving this item?"
            className="w-full bg-[#0F0F0F] border border-[#454038] focus:border-[#D92D20] text-[#E4DFD3] px-2 py-1.5 text-sm font-mono" />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] border border-[#454038] px-3 py-2">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !newStatus}
            data-testid="ef-lifecycle-submit"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20] border border-[#D92D20] hover:bg-[#1a0808] px-3 py-2 disabled:opacity-40 flex items-center gap-1">
            <RotateCw size={11} /> {busy ? "Working…" : "Confirm transition"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminEpsteinLifecycle;
